import React from 'react';

/**
 * FormHeader
 * -----------
 * What:
 *   Sticky header for the RMS wizard pages.
 *
 * Why:
 *   - Keep the form's name visible at all times (left side), so users always
 *     know which form they are completing.
 *   - Use the main H1 to communicate high-level progress at-a-glance:
 *     "Part X/Y: [current section title]".
 *   - Allow an override heading for non-question screens (e.g., Review).
 *
 * Props:
 *  - formTitle: string        // The human-readable name of the form
 *  - partIndex?: number       // 1-based index of the current section
 *  - partTotal?: number       // total number of visible sections
 *  - sectionTitle?: string    // current section name
 *  - rightSlot?: ReactNode    // e.g., saving indicator
 *  - headingRef?: Ref<HTMLElement>  // for focus management on step changes
 *  - hideMeta?: boolean       // when true, hides the meta row (form title/right slot)
 *  - headingOverride?: string // when defined, render this instead of the default H1
 *
 * Accessibility:
 *  - The H1 is the primary page heading. Focus can be moved here by callers
 *    (e.g., when steps change) to ensure a predictable reading order.
 */
export default function FormHeader({
  formTitle = '',
  partIndex = 1,
  partTotal = 1,
  sectionTitle = '',
  rightSlot = null,
  headingRef = null,
  hideMeta = false,
  headingOverride,
}) {
  const headingText =
    typeof headingOverride === 'string' && headingOverride.trim().length > 0
      ? headingOverride
      : `Part ${partIndex} of ${partTotal}: ${sectionTitle}`;

  return (
    <div className="w-full">
      {!hideMeta && (
        <div className="flex items-center justify-between">
          <div className="text-left text-base text-gray-900">
            <span className="text-2xl font-bold">{formTitle}</span>
          </div>
          <div className="text-right">
            {rightSlot}
          </div>
        </div>
      )}
      <h1
        className="text-lg md:text-xl font-semibold text-gray-900 mt-1 text-left"
        tabIndex={-1}
        ref={headingRef}
      >
        {headingText}
      </h1>
    </div>
  );
}


